"use client"

import { useMemo } from "react"

import { asArray } from "@/lib/as-array"
import {
  barsFor,
  leadRole,
  sharedAxis,
  shortRange,
  toRoles,
  type Axis,
  type Role,
} from "@/lib/career-track"
import type { ResultRowData, Verdict } from "@/components/search/graphite-search-results"
import type { CandidateProfile } from "openapi/api/model"

type ResultItem = { profile?: CandidateProfile }

function initialsOf(profile: CandidateProfile | undefined) {
  const first = profile?.first_name?.[0]
  const last = profile?.last_name?.[0]
  if (first || last) return `${first ?? ""}${last ?? ""}`.toUpperCase()
  const parts = (profile?.full_name ?? "").split(/\s+/).filter(Boolean)
  return (parts[0]?.[0] ?? "?").concat(parts[1]?.[0] ?? "").toUpperCase()
}

function locationOf(profile: CandidateProfile | undefined) {
  if (profile?.location_str) return profile.location_str
  return [profile?.city, profile?.country].filter(Boolean).join(", ") || undefined
}

/** "Staff Engineer, Platform · Klarna" — the current role, or the last one held. */
function currentRoleOf(roles: Role[], profile: CandidateProfile | undefined) {
  const latest = leadRole(roles)
  const title = latest?.title ?? profile?.occupation ?? profile?.headline ?? undefined
  const company = latest?.company
  if (title && company) return `${title} · ${company}`
  return title ?? company
}

/**
 * A page of search results as rows on one shared career axis.
 *
 * The axis is the reason this is a hook over the whole page rather than
 * something each row works out for itself: 27a plots every candidate against
 * the same years, and two tracks measured on their own scales would look
 * comparable while comparing nothing.
 *
 * The result list already carries the full profile — experiences included — so
 * none of this costs a request. The shipped card fetched
 * `/search/enrich-profiles/{id}` per row to draw fields the list had already
 * sent; the enrichment is only needed for the match reasoning, which is the
 * drill-down's business.
 */
export function useSearchResultRows({
  candidates,
  pageOrder,
  verdictOf,
  addedIds,
}: {
  /** What is on screen: the page, after filters and hidden results. */
  candidates: ResultItem[] | undefined
  /**
   * The page as the match engine returned it, before anything was filtered or
   * hidden. Both the rank and the axis come from this rather than from what
   * survives: the rank is "where the match engine placed each candidate", and
   * renumbering on a filter makes the second result claim to be the first. The
   * axis is drawn from it for the same reason — a scale that moved every time
   * you hid a row would make two glances at the same track disagree.
   */
  pageOrder?: ResultItem[]
  verdictOf: (candidateId: string) => Verdict
  addedIds: Set<string>
}): { rows: ResultRowData[]; axis: Axis } {
  const items = useMemo(() => asArray<ResultItem>(candidates), [candidates])
  const wholePage = useMemo(
    () => asArray<ResultItem>(pageOrder ?? candidates),
    [candidates, pageOrder],
  )

  const rankById = useMemo(() => {
    const ranks = new Map<string, number>()
    wholePage.forEach((item, index) => {
      const id = String(item.profile?.id ?? "")
      if (id && !ranks.has(id)) ranks.set(id, index + 1)
    })
    return ranks
  }, [wholePage])

  const rolesById = useMemo(
    () =>
      items.map((item) => ({
        id: String(item.profile?.id ?? ""),
        profile: item.profile,
        roles: toRoles(item.profile?.experiences),
      })),
    [items],
  )

  const axis = useMemo(
    () => sharedAxis(wholePage.map((item) => toRoles(item.profile?.experiences))),
    [wholePage],
  )

  const rows = useMemo<ResultRowData[]>(
    () =>
      rolesById.map((entry, index) => ({
        candidateId: entry.id,
        rank: rankById.get(entry.id) ?? index + 1,
        name: entry.profile?.full_name ?? "Unnamed candidate",
        initials: initialsOf(entry.profile),
        location: locationOf(entry.profile),
        currentRole: currentRoleOf(entry.roles, entry.profile),
        bars: barsFor(entry.roles, axis),
        // Newest first in the ledger, which is the order a reader scans.
        roles: entry.roles
          .slice()
          .reverse()
          .map((role) => ({
            key: role.id,
            company: role.company,
            // The shared formatter, not a second spelling of the same fact.
            range: shortRange(role),
            isCurrent: role.isCurrent,
          }))
          .slice(0, 4),
        verdict: verdictOf(entry.id),
        isAdded: addedIds.has(entry.id),
      })),
    [addedIds, axis, rankById, rolesById, verdictOf],
  )

  return { rows, axis }
}
